import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker'

import { useModal } from '@/contexts/ModalContext'

import { ENDPOINTS, sendPostRequest } from '@/services/api'
import { onUncaughtError } from '@/services/userAlerts'
import { SNOWPLOW_SCHEMAS } from '@/services/paths'

import { i18n } from '@/translate/i18n'

import SmallLoading from '@/components/SmallLoading'
import ShareResultView from './ShareResultView'
import ManagementView from './ManagementView'
import TabsView from './TabsView'

import * as S from './styles'

import type {
  RelatedUser,
  RelatedUsersList
} from '@/types/components/Modal/Templates/ShareCollection'

interface SharedResult {
  success: RelatedUsersList
}

export interface ShareCollectionModalConfig {
  collectionId: string
  content_id: string
  type: string
  name: string
  origin_user_email: string
  origin_id: string
  shares: string
  sharedList: RelatedUsersList
  setAction: Dispatch<SetStateAction<boolean>>
  action: boolean
  loading?: boolean
  setLoading: Dispatch<SetStateAction<boolean>>
  module_id?: string | null
}

export interface ShareCollectionModalProps {
  config: ShareCollectionModalConfig
}

const ShareCollectionModal = ({ config }: ShareCollectionModalProps) => {
  const { closeModal } = useModal()

  const { t } = useTranslation()

  const [showLoading, setShowLoading] = useState(true)
  const [allUsers, setAllUsers] = useState<RelatedUsersList>()
  const [getUsers, setGetUsers] = useState<boolean>()
  const [accessList, setAccessList] = useState<RelatedUsersList>(
    config.sharedList
  )
  const [shareResult, setShareResult] = useState<SharedResult>()
  const [activeView, setActiveView] = useState('tabs')
  const [shareLink, setShareLink] = useState(null)
  const [defaultActiveTabIndex, setDefaultActiveTabIndex] = useState(1)

  useEffect(() => {
    async function fetchUsers() {
      setShowLoading(true)
      const body = {
        attributes: ['email', 'cognito_id'],
        collection_id: config.collectionId
      }
      await sendPostRequest(ENDPOINTS.list_related_users, body)
        .then(response => {
          const users = response.data.msg.Items
          setAllUsers(users)
        })
        .catch(error => {
          const msg = error.data || error
          console.log(msg)
          onUncaughtError()
        })
        .finally(() => {
          setShowLoading(false)
        })
    }
    fetchUsers()
  }, [getUsers])

  useEffect(() => {
    ;(async () => {
      const { data } = await sendPostRequest(
        ENDPOINTS.create_collection_share_token,
        {
          collection_id: config.collectionId
        }
      )
      const url = new URL(`${process.env.DOMAIN_URL}/SharedCollection`)
      url.searchParams.append('token', data?.token)
      setShareLink(url)
    })()
  }, [config.collectionId])

  const shareCollection = async (selectedUsers: RelatedUsersList) => {
    trackSelfDescribingEvent({
      event: {
        schema: SNOWPLOW_SCHEMAS.event_button_click,
        data: {
          button_name: SNOWPLOW_SCHEMAS.collection_item_menu_share_confirm
        }
      },
      context: [
        {
          schema: SNOWPLOW_SCHEMAS.entity_collection,
          data: {
            id: config.collectionId,
            content_id: config.content_id,
            type: config.type,
            name: config.name,
            origin_user_email: config.origin_user_email,
            origin_id: config.origin_id,
            shares: `${config.shares}`
          }
        },
        {
          schema: SNOWPLOW_SCHEMAS.entity_module,
          data: {
            content_id: `${config.content_id}`,
            module_id: config.module_id ?? null
          }
        }
      ]
    })

    const body = {
      collection_id: config.collectionId,
      users_ids: selectedUsers.map(item => `${item.cognito_id}`)
    }

    let requestWasSuccessful: boolean

    try {
      await sendPostRequest(ENDPOINTS.share_collection_by_users_ids, body)

      setAccessList(state => state.concat(selectedUsers))

      setShareResult({
        success: selectedUsers
      })
      setActiveView('result')

      requestWasSuccessful = true
    } catch (error) {
      const msg = error.data || error
      console.log(msg)
      onUncaughtError()

      requestWasSuccessful = false
    }

    return requestWasSuccessful
  }

  const unshareCollection = async (value: RelatedUser) => {
    setShowLoading(true)

    const body = {
      collection_id: config.collectionId,
      users_ids: [value.cognito_id]
    }

    let requestWasSuccessful = true

    try {
      await sendPostRequest(ENDPOINTS.unshare_collection_by_users_ids, body)

      setGetUsers(!getUsers)

      Swal.fire({
        icon: 'success',
        title: i18n.t('swal.success'),
        html: i18n.t('messages.collectionUnshared')
      })

      setAccessList(state =>
        state.filter(item => item.cognito_id !== value.cognito_id)
      )
    } catch (error) {
      const msg = error.data || error
      console.log(msg)
      onUncaughtError()

      requestWasSuccessful = false
    }

    setShowLoading(false)

    return requestWasSuccessful
  }

  const closeModalShare = () => {
    config.setAction(state => !state)
    closeModal()
  }

  if (!config) {
    return null
  }

  return (
    <S.SharedListContainer>
      <S.SharedListTitle>
        <p>
          {t('titles.shareCollection')} {config.name}
        </p>
        <button
          id="btn_closemodal_shared"
          type="button"
          onClick={closeModalShare}
        >
          <span className="material-icons">close</span>
        </button>
      </S.SharedListTitle>

      <S.SharedListContent>
        {!showLoading && activeView === 'tabs' && (
          <TabsView
            allUsers={allUsers}
            onShare={shareCollection}
            config={config}
            accessList={accessList}
            onShowManagementView={setActiveView.bind(null, 'management')}
            onCancel={closeModalShare}
            shareLink={shareLink}
            defaultActiveTabIndex={defaultActiveTabIndex}
          />
        )}
        {!showLoading && activeView === 'management' && (
          <ManagementView
            accessList={accessList}
            onClose={() => {
              setDefaultActiveTabIndex(0)
              setActiveView('tabs')
            }}
            onUnshare={unshareCollection}
          />
        )}
        {!showLoading && activeView === 'result' && (
          <ShareResultView
            result={shareResult}
            onClose={closeModalShare}
            onAddNewEmails={() => {
              setDefaultActiveTabIndex(0)
              setActiveView('tabs')
            }}
          />
        )}
      </S.SharedListContent>

      {(showLoading || !shareLink) && <SmallLoading />}
    </S.SharedListContainer>
  )
}

export default ShareCollectionModal
